/* eslint-disable camelcase */

import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { ContentCard } from "cerulean";
import { entities } from "byzantine";
import {
  Button,
  LoadingSkeleton,
  MenuButton,
  Row,
  Tag,
} from "@narmi/design_system";
import { RecipientDrawer, DRAWER_TYPES } from "../../RecipientDrawer";
import DeleteDialog from "./DeleteDialog";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";

interface ManageRecipientPopoverProps {
  handleToggleDeleteDialog: () => void;
  handleToggleEditDrawer: () => void;
}

const ManageRecipientPopover = ({
  handleToggleEditDrawer,
  handleToggleDeleteDialog,
}: ManageRecipientPopoverProps) => {
  const { l10n } = useLocalization();

  return (
    <MenuButton triggerIcon="more-vertical">
      <MenuButton.Item
        label={l10n.getString("button-menu-edit")}
        startIcon="edit-2"
        onSelect={handleToggleEditDrawer}
      />
      <MenuButton.Item
        label={l10n.getString("button-menu-delete")}
        startIcon="trash-2"
        onSelect={handleToggleDeleteDialog}
      />
    </MenuButton>
  );
};

interface RecipientCardProps {
  recipient: API.Recipient;
}

const RecipientCard = ({ recipient }: RecipientCardProps) => {
  const features = useUserFeatures();
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const {
    name,
    nickname,
    fedwire_destination: fedwireDestination,
    ach_destination: achDestination,
    swift_destination: swiftDestination,
  } = recipient;
  const { deleteRecipient } = entities.recipients.useDeleteRecipient();

  const toggleEditDrawer = () => {
    setIsEditDrawerOpen(!isEditDrawerOpen);
  };

  const toggleDeleteDialog = () => {
    setIsDeleteDialogOpen(!isDeleteDialogOpen);
  };

  const handleDeleteRecipient = () => {
    toggleDeleteDialog();
    deleteRecipient(recipient.id);
  };

  return (
    <>
      <ContentCard>
        <div className="recipient-section-header">
          <div
            className="fontColor--heading fontWeight--semibold"
            style={{ overflowWrap: "anywhere" }}
          >
            {name}
          </div>
          <div style={{ marginTop: "-4px" }}>
            <ManageRecipientPopover
              handleToggleDeleteDialog={toggleDeleteDialog}
              handleToggleEditDrawer={toggleEditDrawer}
            />
          </div>
        </div>
        <div className="margin--top--xxs" style={{ height: "20px" }}>
          {nickname}
        </div>
        <div className="margin--top--m">
          {achDestination?.account_number ? (
            <span style={{ marginRight: "6px" }}>
              <Tag kind="subdued" label="ACH" />
            </span>
          ) : null}
          {features.international_wires &&
          (swiftDestination?.bic ||
            swiftDestination?.correspondent_bank_bic) ? (
            <span style={{ marginRight: "6px" }}>
              <Tag kind="subdued" label="Intl wire" />
            </span>
          ) : null}
          {fedwireDestination?.account_number ? (
            <Tag kind="subdued" label="Domestic wire" />
          ) : null}
        </div>
      </ContentCard>
      <DeleteDialog
        recipientName={name}
        recipientType="recipient"
        onDelete={handleDeleteRecipient}
        isOpen={isDeleteDialogOpen}
        closeDialog={() => setIsDeleteDialogOpen(false)}
      />
      <RecipientDrawer
        handleClose={toggleEditDrawer}
        isOpen={isEditDrawerOpen}
        drawerType={DRAWER_TYPES.edit}
        recipientId={recipient.id}
      />
    </>
  );
};

// for now, this will only show ACH recipients
// later on, it will show "universal" recipients (both ACH and wire)
const RecipientsSection = () => {
  const { l10n } = useLocalization();
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const { send: fetchRecipients, loading } =
    entities.recipients.useFetchRecipients();
  const recipients = entities.recipients.useRecipients();

  useEffect(() => {
    fetchRecipients();
  }, []);

  const toggleAddDrawer = () => {
    setIsAddDrawerOpen(!isAddDrawerOpen);
  };

  return (
    <>
      <div className="margin--top--xl margin--bottom--l">
        <Row>
          <Row.Item>
            <div className="fontSize--l fontColor--heading fontWeight--bold">
              {l10n.getString("heading-recipients")}
            </div>
          </Row.Item>
          <Row.Item shrink>
            <Button
              kind="plain"
              label={l10n.getString("button-add-recipient")}
              onClick={toggleAddDrawer}
            />
          </Row.Item>
        </Row>
      </div>
      <LoadingSkeleton content="paragraph" isLoading={loading}>
        <div
          className={
            recipients?.length ? "recipients-container" : "no-recipients"
          }
        >
          {recipients?.map((recipient) => (
            <RecipientCard key={recipient.id} recipient={recipient} />
          ))}
          {!recipients?.length && (
            <div>{l10n.getString("message-no-recipients")}</div>
          )}
        </div>
      </LoadingSkeleton>
      <RecipientDrawer
        handleClose={toggleAddDrawer}
        isOpen={isAddDrawerOpen}
        drawerType={DRAWER_TYPES.add}
      />
    </>
  );
};

export default RecipientsSection;
